import type { Article, Block } from './types';

export const SITE_URL = 'https://www.telegram-stars.io';
export const SITE_NAME = 'Telegram Stars Payment Platform';

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Converts **bold**, `code` and [label](url) into HTML. Escapes everything else. */
export function inline(text: string): string {
  let out = '';
  let rest = text;
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/;
  while (rest.length) {
    const m = re.exec(rest);
    if (!m || m.index === undefined) {
      out += escapeHtml(rest);
      break;
    }
    out += escapeHtml(rest.slice(0, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) {
      out += `<strong>${inline(tok.slice(2, -2))}</strong>`;
    } else if (tok.startsWith('`')) {
      out += `<code>${escapeHtml(tok.slice(1, -1))}</code>`;
    } else {
      const lm = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(tok);
      if (lm) {
        const href = lm[2];
        const external = /^https?:\/\//.test(href) && !href.startsWith(SITE_URL);
        out += `<a href="${escapeHtml(href)}"${external ? ' target="_blank" rel="noopener"' : ''}>${escapeHtml(lm[1])}</a>`;
      } else {
        out += escapeHtml(tok);
      }
    }
    rest = rest.slice(m.index + tok.length);
  }
  return out;
}

export function slugify(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function articleLang(a: Article): 'en' | 'fr' {
  return a.lang === 'fr' ? 'fr' : 'en';
}

/** Visible chrome strings for an article page, in the article language. */
export function articleUi(lang: 'en' | 'fr') {
  if (lang === 'fr') {
    return {
      home: 'Accueil',
      published: 'Publié le',
      updated: 'Mis à jour le',
      minRead: 'min de lecture',
      takeaways: 'À retenir',
      faq: 'Questions fréquentes',
      inThisArticle: 'Dans cet article',
      related: 'Articles liés',
      readMore: 'Lire la suite',
      sideTitle: 'Encaissez en Stars',
      sideText: 'Un lien de paiement en quelques secondes. 3 % de frais, retrait en TON dès 100 Stars.',
      sideCta: 'Créer un compte gratuit',
      ctaTitle: 'Prêt à encaisser des Telegram Stars ?',
      ctaBody:
        'Créez un compte gratuit, générez un lien de paiement et recevez des Stars. Sans code, sans abonnement, 3 % par transaction.',
      ctaStart: 'Commencer',
      ctaHow: 'voir comment ça marche',
      htmlLang: 'fr',
      ogLocale: 'fr_FR',
      languageName: 'French',
    };
  }
  return {
    home: 'Home',
    published: 'Published',
    updated: 'Updated',
    minRead: 'min read',
    takeaways: 'Key takeaways',
    faq: 'Frequently asked questions',
    inThisArticle: 'In this article',
    related: 'Related articles',
    readMore: 'Read more',
    sideTitle: 'Get paid in Stars',
    sideText: 'Payment links in minutes. 3% fee, TON payouts from 100 Stars.',
    sideCta: 'Create free account',
    ctaTitle: 'Ready to accept Telegram Stars?',
    ctaBody:
      'Create a free account, generate a payment link and start receiving Stars today. No code, no monthly fee, 3% per transaction.',
    ctaStart: 'Get started',
    ctaHow: 'see how it works',
    htmlLang: 'en',
    ogLocale: 'en_US',
    languageName: 'English',
  };
}

export function blockToHtml(b: Block): string {
  switch (b.t) {
    case 'h2':
      return `<h2 id="${slugify(b.text)}">${inline(b.text)}</h2>`;
    case 'h3':
      return `<h3 id="${slugify(b.text)}">${inline(b.text)}</h3>`;
    case 'p':
      return `<p>${inline(b.text)}</p>`;
    case 'ul':
      return `<ul>${b.items.map((i) => `<li>${inline(i)}</li>`).join('')}</ul>`;
    case 'ol':
      return `<ol>${b.items.map((i) => `<li>${inline(i)}</li>`).join('')}</ol>`;
    case 'callout':
      return `<aside class="article-callout">${inline(b.text)}</aside>`;
    case 'code':
      return `<pre><code${b.lang ? ` class="language-${b.lang}"` : ''}>${escapeHtml(b.code)}</code></pre>`;
    case 'table':
      return (
        `<div class="article-table-wrap"><table><thead><tr>${b.head.map((h) => `<th>${inline(h)}</th>`).join('')}</tr></thead>` +
        `<tbody>${b.rows.map((r) => `<tr>${r.map((c) => `<td>${inline(c)}</td>`).join('')}</tr>`).join('')}</tbody></table></div>`
      );
    default:
      return '';
  }
}

export function bodyToHtml(blocks: Block[]): string {
  return blocks.map(blockToHtml).join('\n');
}

export function tableOfContents(a: Article): { id: string; text: string }[] {
  return a.body
    .filter((b): b is Extract<Block, { t: 'h2' }> => b.t === 'h2')
    .map((b) => ({ id: slugify(b.text), text: b.text }));
}

export function articleUrl(a: Article): string {
  // Trailing slash: on Render, only /path/ serves the prerendered HTML;
  // /path is swallowed by the SPA rewrite to the homepage shell.
  return `${SITE_URL}/blog/${a.slug}/`;
}

export function formatDate(iso: string, locale = 'en-US'): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
}

/** JSON-LD graph for an article page (BlogPosting + FAQPage + Breadcrumb + Organization). */
export function articleJsonLd(a: Article): Record<string, unknown>[] {
  const url = articleUrl(a);
  const org = {
    '@type': 'Organization',
    '@id': `${SITE_URL}/#organization`,
    name: SITE_NAME,
    url: SITE_URL,
    logo: { '@type': 'ImageObject', url: `${SITE_URL}/images/logo.png` },
  };
  const graph: Record<string, unknown>[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      '@id': `${url}#article`,
      mainEntityOfPage: { '@type': 'WebPage', '@id': url },
      headline: a.title,
      description: a.metaDescription,
      keywords: a.keywords.join(', '),
      articleSection: a.category,
      inLanguage: articleLang(a),
      wordCount: wordCount(a),
      datePublished: a.datePublished,
      dateModified: a.dateModified,
      author: org,
      publisher: org,
      image: `${SITE_URL}/images/og-image.png`,
      isPartOf: { '@type': 'Blog', '@id': `${SITE_URL}/blog/#blog`, name: `${SITE_NAME} Blog` },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog/` },
        { '@type': 'ListItem', position: 3, name: a.title, item: url },
      ],
    },
  ];
  if (a.faq.length) {
    graph.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      inLanguage: articleLang(a),
      mainEntity: a.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }
  if (a.howTo && a.howTo.steps.length) {
    graph.push({
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: a.howTo.name,
      inLanguage: articleLang(a),
      step: a.howTo.steps.map((s, i) => ({
        '@type': 'HowToStep',
        position: i + 1,
        name: s.name,
        text: s.text,
      })),
    });
  }
  return graph;
}

export function blogIndexJsonLd(articles: Article[]): Record<string, unknown>[] {
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      '@id': `${SITE_URL}/blog/#blog`,
      url: `${SITE_URL}/blog/`,
      name: `${SITE_NAME} Blog`,
      description:
        'Guides and tutorials on Telegram Stars payments: accepting Stars, payment links, fees, withdrawals to TON, Bot API and monetization.',
      inLanguage: 'en',
      publisher: { '@type': 'Organization', '@id': `${SITE_URL}/#organization`, name: SITE_NAME, url: SITE_URL },
      blogPost: articles.map((a) => ({
        '@type': 'BlogPosting',
        '@id': `${articleUrl(a)}#article`,
        headline: a.title,
        url: articleUrl(a),
        datePublished: a.datePublished,
        dateModified: a.dateModified,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${SITE_URL}/blog/` },
      ],
    },
  ];
}

export function wordCount(a: Article): number {
  const text = [
    a.title,
    a.excerpt,
    ...a.keyTakeaways,
    ...a.body.map((b) => {
      if (b.t === 'ul' || b.t === 'ol') return b.items.join(' ');
      if (b.t === 'table') return [...b.head, ...b.rows.flat()].join(' ');
      if (b.t === 'code') return b.code;
      return b.text;
    }),
    ...a.faq.map((f) => `${f.q} ${f.a}`),
  ].join(' ');
  return text.split(/\s+/).filter(Boolean).length;
}
